import { useEffect, useState } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { Link } from 'react-router-dom';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://example.com';

const FaqPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const [openIndex, setOpenIndex] = useState<number | null>(0);

  const faqs = [
    {
      question: '見積もりは無料ですか？',
      answer: 'はい、お見積もりは完全無料です。現地調査を行い、詳細なお見積もりをご提示いたします。お見積もり後のキャンセルも可能ですので、お気軽にご相談ください。',
    },
    {
      question: '対応エリアはどこまでですか？',
      answer: '岡山市（北区・中区・南区・東区）を中心に、倉敷市やその他近隣市町村にも対応しております。詳しい対応エリアについては、お電話またはお問い合わせフォームからご相談ください。',
    },
    {
      question: '工事期間はどのくらいかかりますか？',
      answer: '工事内容により異なりますが、小規模な修理であれば即日〜3日程度、内装工事で2日〜1週間程度、大規模なリフォームで1週間〜1ヶ月程度が目安となります。お見積もり時に詳しい工期をお伝えいたします。',
    },
    {
      question: '小さな修理でも対応してもらえますか？',
      answer: 'もちろん対応いたします。ドアの建付け調整や、壁の小さな穴の補修など、どんな小さな工事でもお気軽にご相談ください。小さな修理こそ、経験豊富な職人の技術が活きます。',
    },
    {
      question: '支払い方法は何がありますか？',
      answer: '現金でのお支払いのほか、銀行振込にも対応しております。工事完了後のお支払いとなります。大規模な工事の場合は、着手金と完了後の分割払いも可能ですので、ご相談ください。',
    },
    {
      question: '保証はありますか？',
      answer: 'はい、工事内容に応じて保証期間を設けております。施工後に不具合が生じた場合は、無償で対応いたします。保証内容の詳細は、お見積もり時にご説明いたします。',
    },
    {
      question: '急な依頼にも対応できますか？',
      answer: '可能な限り対応いたします。雨漏りや水漏れなどの緊急性の高い修理は、最優先で対応いたします。まずはお電話でご相談ください。状況に応じて即日対応も可能です。',
    },
    {
      question: '相談だけでも大丈夫ですか？',
      answer: 'もちろん大丈夫です。「こんな工事はできるのか」「だいたいいくらくらいかかるのか」など、どんなご相談でも歓迎いたします。お電話またはお問い合わせフォームからお気軽にご連絡ください。',
    },
  ];

  useEffect(() => {
    const webPageSchema = {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      '@id': `${SITE_URL}/faq#faqpage`,
      url: `${SITE_URL}/faq`,
      name: 'よくある質問｜岡山市の工務店・リフォーム｜株式会社おかやま工務店',
      description: '岡山市の株式会社おかやま工務店へのよくある質問。見積もり・対応エリア・工事期間・保証など詳しくお答えします。',
      inLanguage: 'ja',
      isPartOf: { '@id': `${SITE_URL}/#website` },
      dateModified: new Date().toISOString().split('T')[0],
      speakable: {
        '@type': 'SpeakableSpecification',
        cssSelector: ['h1', 'h2'],
      },
      breadcrumb: {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'ホーム', item: `${SITE_URL}/` },
          { '@type': 'ListItem', position: 2, name: 'よくある質問', item: `${SITE_URL}/faq` },
        ],
      },
      mainEntity: faqs.map((faq) => ({
        '@type': 'Question',
        name: faq.question,
        acceptedAnswer: {
          '@type': 'Answer',
          text: faq.answer,
        },
      })),
    };
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'schema-faq';
    script.textContent = JSON.stringify(webPageSchema);
    document.head.appendChild(script);
    return () => {
      const el = document.getElementById('schema-faq');
      if (el) el.remove();
    };
  }, []);

  const toggleFaq = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero Section */}
      <section className="relative py-24 lg:py-32 px-6 bg-[#faf8f3] mt-20">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-[#1a1a2e] leading-tight mb-6" style={{ fontFamily: "'Noto Sans JP', sans-serif" }}>
            よくある質問
          </h1>
          <p className="text-lg text-gray-700 leading-relaxed">
            お客様からよくいただくご質問にお答えします
          </p>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 lg:py-24 px-6 bg-white">
        <div className="max-w-4xl mx-auto">
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div
                key={index}
                className="bg-white border-2 border-gray-200 rounded-2xl overflow-hidden hover:border-[#ff6b35] transition-colors duration-300"
              >
                <button
                  onClick={() => toggleFaq(index)}
                  className="w-full px-6 lg:px-8 py-6 flex items-start justify-between text-left cursor-pointer hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-start flex-1">
                    <span className="flex-shrink-0 w-10 h-10 flex items-center justify-center bg-[#ff6b35] text-white font-bold rounded-lg mr-4 text-lg">
                      Q
                    </span>
                    <h3 className="text-lg lg:text-xl font-bold text-[#1a1a2e] leading-relaxed pt-1">
                      {faq.question}
                    </h3>
                  </div>
                  <i
                    className={`ri-arrow-down-s-line text-2xl text-[#1a1a2e] ml-4 flex-shrink-0 transition-transform duration-300 ${
                      openIndex === index ? 'rotate-180' : ''
                    }`}
                  ></i>
                </button>

                <div
                  className={`overflow-hidden transition-all duration-300 ${
                    openIndex === index ? 'max-h-96' : 'max-h-0'
                  }`}
                >
                  <div className="px-6 lg:px-8 pb-6">
                    <div className="flex items-start pl-14">
                      <span className="flex-shrink-0 w-10 h-10 flex items-center justify-center bg-[#1a1a2e] text-white font-bold rounded-lg mr-4 text-lg">
                        A
                      </span>
                      <p className="text-base text-gray-700 leading-relaxed pt-2">
                        {faq.answer}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 lg:py-24 px-6 bg-[#faf8f3]">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-[#1a1a2e] mb-6" style={{ fontFamily: "'Noto Sans JP', sans-serif" }}>
            その他のご質問はお気軽に
          </h2>
          <p className="text-lg text-gray-700 mb-10 leading-relaxed">
            上記以外のご質問やご不明な点がございましたら、<br />
            お電話またはお問い合わせフォームからお気軽にご連絡ください
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="tel:086-012-3456"
              className="inline-flex items-center justify-center px-8 py-4 bg-[#ff6b35] text-white text-base lg:text-lg font-bold rounded-full hover:bg-[#ff8555] transition-all duration-300 hover:scale-105 shadow-lg whitespace-nowrap cursor-pointer"
            >
              <i className="ri-phone-fill mr-2"></i>
              086-012-3456
            </a>
            <Link
              to="/contact"
              className="inline-flex items-center justify-center px-8 py-4 bg-[#1a1a2e] text-white text-base lg:text-lg font-bold rounded-full hover:bg-[#2a2a3e] transition-all duration-300 hover:scale-105 whitespace-nowrap"
            >
              <i className="ri-mail-line mr-2"></i>
              お問い合わせフォーム
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default FaqPage;
